/**
 * CLE-2884 — which side of a contract a rate file belongs on: the base rate rows
 * (`rateCalculations`) or Carve-Outs. The API refuses a carve-out file sent as a base rate row
 * ("Carveout files must be attached under Carve-Outs, not as base rate files on rate
 * calculations."), so the contract forms must route each file by its own `isCarveout` flag.
 *
 * The forms used to learn that flag only from the base rate files list query. A file uploaded
 * from the form's "+ Upload new file…" option is selected before that list refetches, so it was
 * unknown at Add time, landed in the base rate rows, and every save after that failed the same
 * way. The upload response already carries the flag; these helpers let the form use it, and let
 * the save refuse — naming the file — instead of sending a body the API will reject.
 *
 * Pure, so each rule is tested without a store or a DOM.
 */

import { IBaseRateFile } from "types/baseRateFile";

export type RateFileKind = "carveout" | "base" | "unknown";

/** The part of a base rate file these helpers read. */
interface IKnownRateFile {
  displayName?: string;
  isCarveout?: boolean;
}

interface IRateRowFile {
  baseRateFileId?: string | null;
}

/**
 * The files the form knows about: the list query's rows, then any file uploaded on this page
 * that the list does not contain yet. The list wins on an id both have — it is the server's
 * current view, so a later conversion shows there and never in the upload response. With no
 * uploads this is exactly the list, in its order.
 */
export const mergeKnownBaseRateFiles = (
  listed: IBaseRateFile[] | undefined,
  uploaded: IBaseRateFile[]
): IBaseRateFile[] => {
  const list = listed ?? [];
  const seen = new Set(list.map((f) => f.id));
  const fresh = uploaded.filter((f) => {
    if (seen.has(f.id)) return false;
    seen.add(f.id);
    return true;
  });
  return fresh.length === 0 ? list : [...list, ...fresh];
};

/** Carve-out or base rate file, or "unknown" when the form has never seen the id. */
export const rateFileKind = (
  id: string | undefined | null,
  known: ReadonlyMap<string, IKnownRateFile>
): RateFileKind => {
  if (!id) return "unknown";
  const file = known.get(id);
  if (!file) return "unknown";
  return file.isCarveout ? "carveout" : "base";
};

/**
 * Names of the known carve-out files sitting in the base rate rows — what a save would send the
 * API as base rate files and have refused. De-duplicated; the id stands in for a missing name.
 *
 * A file the form does not know is never reported: blocking on a guess would refuse saves the
 * API accepts. Neither is a file in `savedRows` — the rows the contract already had when the
 * page loaded. The API only re-checks rate rows when the set changes, so a stored row it once
 * accepted is not this form's to refuse.
 */
export const carveoutFilesInRateRows = (
  rateCalculations: IRateRowFile[],
  known: ReadonlyMap<string, IKnownRateFile>,
  savedRows: IRateRowFile[] = []
): string[] => {
  const savedIds = new Set(
    savedRows
      .map((row) => row.baseRateFileId)
      .filter((id): id is string => typeof id === "string" && id !== "")
  );
  const names: string[] = [];
  rateCalculations.forEach((row) => {
    const id = row.baseRateFileId;
    if (!id || savedIds.has(id)) return;
    if (rateFileKind(id, known) !== "carveout") return;
    const name = known.get(id)?.displayName?.trim() || id;
    if (!names.includes(name)) names.push(name);
  });
  return names;
};

/** The toast a save shows instead of sending carve-out files as base rate rows; null when none. */
export const misroutedCarveoutWarning = (
  names: string[]
): { summary: string; detail: string } | null => {
  if (names.length === 0) return null;
  const quoted = names.map((name) => `“${name}”`).join("; ");
  if (names.length === 1) {
    return {
      summary: "Carve-out file in base rate rows",
      detail: `${quoted} is a carve-out file. Remove it from the rate rows and add it again — it will attach under Carve-Outs.`,
    };
  }
  return {
    summary: "Carve-out files in base rate rows",
    detail: `${quoted} are carve-out files. Remove them from the rate rows and add them again — they will attach under Carve-Outs.`,
  };
};
